import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import SendIcon from '@mui/icons-material/Send';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import moment from 'moment';
import firebase from '../../utilities/firebase';
import { useDatePickerSlotProps } from '../../utilities/ModalStackContext';

import { LicenseSlot } from './SalesComponents/LicenseUpload';
import FinanceInvoicePanel from './FinanceComponents/FinanceInvoicePanel';
import FinanceDepositsPanel from './FinanceComponents/FinanceDepositsPanel';
import NoteList from './FinanceComponents/NoteList';
import SendStipsDialog from '../SendStipsDialog';
import SendToLendersDialog from './FinanceComponents/SendToLendersDialog';
import store from '../../utilities/store';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const DATE_FORMATS = ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD'];

export default function Finance(props) {
  const { car = {} } = props;
  const stockNumber = car.stock;
  const deal = car.deal || {};
  const datePickerSlotProps = useDatePickerSlotProps();

  const [invoices, setInvoices] = React.useState(car.finance_invoices || []);
  const [activeInvoiceId, setActiveInvoiceId] = React.useState(deal.finance);
  const [extraDeposits, setExtraDeposits] = React.useState([]);
  const [deletedDepositIds, setDeletedDepositIds] = React.useState([]);
  const [updatedDeposits, setUpdatedDeposits] = React.useState({});
  const [stipsOpen, setStipsOpen] = React.useState(false);
  const [lendersOpen, setLendersOpen] = React.useState(false);
  const [stipSends, setStipSends] = React.useState([]);

  React.useEffect(() => { setInvoices(car.finance_invoices || []); }, [car.finance_invoices]);
  React.useEffect(() => { setActiveInvoiceId(deal.finance); }, [deal.finance]);

  // Show what already went to the banks, so a resend is an informed decision.
  const loadStipSends = React.useCallback(() => {
    if (!stockNumber) return;
    store.getStipSends({ stockNumber })
      .then(setStipSends)
      .catch(() => setStipSends([]));
  }, [stockNumber]);

  React.useEffect(() => { loadStipSends(); }, [loadStipSends]);

  // Writes go straight to Firestore and are mirrored into the shared `car` state
  // so the rest of the modal reflects them without a reload.
  const updateSource = (target, id, value) => {
    const newCar = { ...car };
    newCar[target] = { ...newCar[target], [id]: value };
    car.updater?.(newCar);
  };

  const carUpdater = (id, value) => {
    firebase.firestore().doc('cars/' + stockNumber).set({ [id]: value }, { merge: true });
    car.updater?.({ ...car, [id]: value });
  };

  const dealUpdater = (id, value) => {
    firebase.firestore().doc('deals/' + stockNumber).set({ [id]: value }, { merge: true });
    updateSource('deal', id, value);
  };

  const activeInvoice = invoices.find(inv => inv.id === activeInvoiceId) || null;
  const invoiceTotal = activeInvoice?.total || 0;

  const deposits = [...(car.finance_deposits || []), ...extraDeposits]
    .filter(d => !d.id || !deletedDepositIds.includes(d.id))
    .map(d => (d.id && updatedDeposits[d.id]) ? { ...d, ...updatedDeposits[d.id] } : d);
  const collected = deposits.reduce((s, d) => s + (d.amount || 0), 0);
  const remaining = invoiceTotal - collected;

  // Default the receiving bank to whatever this car last used, so the common case
  // is a confirmation rather than a decision.
  const defaultAccount = React.useMemo(() => {
    const candidates = [...deposits, ...(car.deposits || [])]
      .filter(d => d.account)
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    return candidates[0]?.account || 'mercury';
  }, [deposits, car.deposits]);

  const appSentDate = car.app_sent_customer;
  const parsedAppSent = appSentDate ? moment(appSentDate, DATE_FORMATS) : null;

  const chips = [
    { label: 'Finance Deal', done: !!deal.is_finance },
    { label: 'Approved', done: !!deal.approved },
    { label: 'Invoiced', done: invoiceTotal > 0 },
    { label: 'Funded', done: invoiceTotal > 0 && remaining <= 0 },
    { label: 'Complete', done: !!car.finance_complete },
  ];

  return (
    <Box className="no-print" sx={{ mx: -2, mt: -2 }}>

      {/* Sticky status bar */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider',
        px: { xs: 2, sm: 3 }, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {chips.map(({ label, done }) => (
            <Chip
              key={label}
              label={label}
              size="small"
              icon={done
                ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />}
              color={done ? 'success' : 'default'}
              variant={done ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
        {invoiceTotal > 0 && (
          <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
            {USD(collected)} / {USD(invoiceTotal)}
          </Typography>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => setLendersOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Send to Lenders
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ForwardToInboxIcon />}
          onClick={() => setStipsOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Send Stips to Bank
        </Button>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Grid container spacing={2}>

          {/* Left column — the application */}
          <Grid item xs={12} md={6}>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <SectionHeader>Application</SectionHeader>
              <Box sx={{ px: 2, py: 1 }}>
                <ToggleRow
                  label="Is Finance Deal?"
                  checked={!!deal.is_finance}
                  onChange={(v) => dealUpdater('is_finance', v)}
                />
                <Divider />
                <ToggleRow
                  label="Customer Approved"
                  checked={!!deal.approved}
                  onChange={(v) => dealUpdater('approved', v)}
                />
                <Divider />
                <Box sx={{ py: 1.5 }}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="App Sent to Customer"
                      value={parsedAppSent && parsedAppSent.isValid() ? parsedAppSent : null}
                      onChange={(newValue) => {
                        const formatted = newValue ? moment(newValue).format('YYYY/MM/DD') : null;
                        // `is_app_sent_customer` is the companion flag the older
                        // DateLine form wrote alongside the date — keep them in step.
                        firebase.firestore().doc('cars/' + stockNumber).set({
                          app_sent_customer: formatted,
                          is_app_sent_customer: !!formatted,
                        }, { merge: true });
                        car.updater?.({ ...car, app_sent_customer: formatted, is_app_sent_customer: !!formatted });
                      }}
                      slotProps={{
                        field: { clearable: true },
                        textField: { size: 'small', fullWidth: true },
                        popper: datePickerSlotProps.popper,
                        dialog: datePickerSlotProps.dialog,
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </Paper>

            {/* Buyer documents — license front/back and proof of insurance */}
            {car.buyer?.id && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <SectionHeader>Buyer Documents</SectionHeader>
                <Box sx={{ px: 2, py: 2, display: 'flex', gap: 1.5 }}>
                  <LicenseSlot label="License Front" fieldId="license" customerId={car.buyer.id} data={car.buyer} />
                  <LicenseSlot label="License Back" fieldId="license_back" customerId={car.buyer.id} data={car.buyer} />
                  <LicenseSlot label="Insurance" fieldId="insurance" customerId={car.buyer.id} data={car.buyer} />
                </Box>
              </Paper>
            )}

            {/* Underwriting checklists */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <SectionHeader>Underwriting</SectionHeader>
              <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <SubSection label="Banks Submitted">
                  <NoteList id={stockNumber} table="deals" property="banks" entity="bank" items={deal.banks || []} />
                </SubSection>
                <SubSection label="Stips Needed">
                  <NoteList id={stockNumber} table="deals" property="stips" entity="stip" items={deal.stips || []} />
                </SubSection>
                <SubSection label="Funding Stips">
                  <NoteList id={stockNumber} table="deals" property="funding_stips" entity="stip" items={deal.funding_stips || []} />
                </SubSection>
                <SubSection label="Stips Sent to Banks">
                  <StipSendSummary sends={stipSends} onResend={() => setStipsOpen(true)} />
                </SubSection>
              </Box>
            </Paper>
          </Grid>

          {/* Right column — the money */}
          <Grid item xs={12} md={6}>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <SectionHeader>Finance Invoice</SectionHeader>
              <FinanceInvoicePanel
                invoices={invoices}
                deal={{ ...deal, finance: activeInvoiceId }}
                stockNumber={stockNumber}
                defaultAccount={defaultAccount}
                onInvoicesChanged={(next, nextActiveId) => {
                  setInvoices(next);
                  setActiveInvoiceId(nextActiveId);
                }}
                onDepositAdded={(d) => setExtraDeposits(prev => [...prev, d])}
              />
            </Paper>

            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <SectionHeader>Deposits</SectionHeader>
              <FinanceDepositsPanel
                deposits={deposits}
                stockNumber={stockNumber}
                invoiceTotal={invoiceTotal}
                defaultAccount={defaultAccount}
                onDepositAdded={(d) => setExtraDeposits(prev => [...prev, d])}
                onDepositDeleted={(id) => setDeletedDepositIds(prev => [...prev, id])}
                onDepositUpdated={(d) => setUpdatedDeposits(prev => ({ ...prev, [d.id]: d }))}
              />
            </Paper>

            {/* Summary tiles */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" sx={{ fontSize: 10, color: 'text.secondary', display: 'block' }}>
                    Invoice Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{USD(invoiceTotal)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" sx={{ fontSize: 10, color: 'text.secondary', display: 'block' }}>
                    {remaining >= 0 ? 'Remaining' : 'Over-collected'}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: remaining > 0 ? 'warning.dark' : 'success.dark' }}
                  >
                    {USD(Math.abs(remaining))}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 0.5 }}>
                <ToggleRow
                  label="All Finance Tasks Complete"
                  checked={!!car.finance_complete}
                  onChange={(v) => carUpdater('finance_complete', v)}
                />
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </Box>

      <SendStipsDialog
        open={stipsOpen}
        onClose={() => setStipsOpen(false)}
        stockNumber={stockNumber}
        onSent={loadStipSends}
      />

      <SendToLendersDialog
        open={lendersOpen}
        onClose={() => setLendersOpen(false)}
        car={car}
      />
    </Box>
  );
}

// Compact record of what went to which bank and when. The dialog holds the full
// history and the resend controls; this is the at-a-glance version.
function StipSendSummary({ sends = [], onResend }) {
  if (!sends.length) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Nothing sent yet
      </Typography>
    );
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      {sends.slice(0, 4).map((send, i) => (
        <Box
          key={send.id}
          sx={{
            px: 1, py: 0.75,
            borderBottom: i < Math.min(sends.length, 4) - 1 ? 1 : 0, borderColor: 'divider',
            bgcolor: i % 2 === 1 ? 'grey.50' : 'transparent',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
              {(send.banks || []).map(b => b.name).join(', ') || 'Unknown lender'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
              {send.sentAt?.seconds ? moment.unix(send.sentAt.seconds).format('MMM D, YYYY') : '—'}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {(send.documents || []).map(d => d.label).join(' · ') || 'No documents recorded'}
          </Typography>
        </Box>
      ))}
      <Box sx={{ px: 1, py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {sends.length > 4 ? `${sends.length - 4} earlier send(s)` : ''}
        </Typography>
        <Button size="small" onClick={onResend}>Send again</Button>
      </Box>
    </Box>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        px: 2, pt: 1.5, pb: 0.5,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.8,
        color: 'text.secondary',
        borderBottom: 1, borderColor: 'divider',
      }}
    >
      {children}
    </Typography>
  );
}

function SubSection({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function ToggleRow({ label, checked, onChange, disabled }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2">{label}</Typography>
      <Switch
        size="small"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
    </Box>
  );
}
